import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

// Get API URL based on environment
const getApiUrl = () => {
  const env = import.meta.env.VITE_APP_ENV || 'develop';

  switch (env) {
    case 'production':
      return import.meta.env.VITE_API_URL_PRODUCTION;
    case 'staging':
      return import.meta.env.VITE_API_URL_STAGING;
    default:
      return import.meta.env.VITE_API_URL_DEV || 'http://localhost:8000';
  }
};

export const BASE_URL = getApiUrl();

const headers = {
  'Content-Type': 'application/json'
};

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers
});

// Track refresh state to prevent multiple concurrent refresh requests
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Track logout state to prevent refresh during logout
let isLoggingOut = false;
let logoutPromise: Promise<void> | null = null;

// Callback to trigger logout from AuthContext
let logoutCallback: (() => Promise<void>) | null = null;

// Callback to refresh token from AuthContext
let refreshTokenCallback: (() => Promise<string>) | null = null;

/**
 * Set the logout callback - called by AuthContext on mount
 */
export const setLogoutCallback = (callback: (() => Promise<void>) | null) => {
  logoutCallback = callback;
};

/**
 * Set the refresh token callback - called by AuthContext on mount
 */
export const setRefreshTokenCallback = (callback: (() => Promise<string>) | null) => {
  refreshTokenCallback = callback;
};

/**
 * Set the authorization token for all requests
 */
export const setAuthToken = (token: string | null) => {
  if (token) {
    client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete client.defaults.headers.common['Authorization'];
  }
};

/**
 * Get the current auth token from localStorage
 */
export const getStoredToken = (): string | null => {
  return localStorage.getItem('@auth_token');
};

/**
 * Mark that logout is in progress
 */
export const setIsLoggingOut = (value: boolean) => {
  isLoggingOut = value;
  if (!value) {
    logoutPromise = null;
  }
};

// Request interceptor
client.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - handles token refresh on 401/403
 *
 * When a 401 (Unauthorized) or 403 (Forbidden) response is received:
 * 1. Attempts to refresh the access token using the refresh token
 * 2. If refresh succeeds, retries the original request with the new token
 * 3. If refresh fails (e.g., refresh token expired), automatically logs out the user
 */
client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _skipAuthRetry?: boolean;
    };

    const status = error.response?.status;

    // Handle both 401 (Unauthorized) and 403 (Forbidden) - both indicate auth issues
    if ((status === 401 || status === 403) && originalRequest && !originalRequest._retry) {
      // Don't retry if explicitly marked to skip (e.g., refresh endpoint itself)
      if (originalRequest._skipAuthRetry) {
        return Promise.reject(error);
      }

      // Don't attempt refresh if we're currently logging out
      if (isLoggingOut) {
        return Promise.reject(error);
      }

      // If logout is already in progress, wait for it to complete
      if (logoutPromise) {
        await logoutPromise;
        return Promise.reject(error);
      }

      // Mark request as retried to prevent infinite loops
      originalRequest._retry = true;

      try {
        // If already refreshing, wait for the existing refresh promise
        if (isRefreshing && refreshPromise) {
          const newToken = await refreshPromise;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return client(originalRequest);
        }

        // Start refresh process if we have a refresh callback
        if (refreshTokenCallback) {
          isRefreshing = true;
          refreshPromise = refreshTokenCallback();

          const newToken = await refreshPromise;

          // Update the authorization header for the retry
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          // Reset refresh state
          isRefreshing = false;
          refreshPromise = null;

          // Retry the original request with new token
          return client(originalRequest);
        } else {
          // No refresh callback available, trigger logout
          if (logoutCallback && !isLoggingOut) {
            console.error('No refresh token callback - logging out');
            isLoggingOut = true;
            logoutPromise = (async () => {
              try {
                await logoutCallback();
              } finally {
                isLoggingOut = false;
                logoutPromise = null;
              }
            })();
            await logoutPromise;
          }
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Token refresh failed, trigger logout
        console.error('Token refresh failed - logging out user');
        isRefreshing = false;
        refreshPromise = null;

        if (logoutCallback && !isLoggingOut) {
          isLoggingOut = true;
          logoutPromise = (async () => {
            try {
              await logoutCallback();
            } finally {
              isLoggingOut = false;
              logoutPromise = null;
            }
          })();
          await logoutPromise;
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default client;

// Re-export all API modules
export * as authApi from './auth';
export * as usersApi from './users';
export * as businessApi from './business';
